import React, { useState } from 'react'
import GenericModal from '../../../../components/GenericModal'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { useAppDispatch } from '../../../../store'
import { type IDistributionPlan, SlotStatus, type ISlot } from '../../../../models/DistributionPlan'
import { getSlotSectionApi } from '../../../../store/slices/distributionPlanSlice/apis'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Dropdown, { type DropdownItemConfig } from '../../../../components/Dropdown'
import { CreateAndEditCourses } from '../CreateAndEditCourses'
import { notification } from '../../../../components/Notifications'
import { Actions, TableModalAction, type ApplyFiltersAndDispatchParams } from '../../../../components/CustomTable/types'
import { type ApiError } from '../../../../models/Common'
import { DistributionManager } from '../../../../APIManager'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { grey } from '../../../../colors'
import SyncSectionModal from '../../SyncSectionModal'
import { viewFromStatus } from '../../ProjectSectionTab'
import { useParams, useSearchParams } from 'react-router-dom'
import { useSlotTaskContext } from '../../../../contexts/SlotTaskContext'
import { createUpdateTemplateRevisionSuccessHandler } from '../templateRevisionHandlers'
import { UpdateTemplateRevisionProjectModal } from '../UpdateTemplateRevisionProjectModal'
import BulkSelectCourseSectionModal from '../BulkAddCourseSectionModal'
import { isBulkGoLiveEnabled, isBulkReopenEnabled } from '../bulkSectionStatusActions'
import {
  CSV_INSTRUCTOR_RELEASE_CLEAR_TOKEN,
  isInstructorReleaseOnGradePostingEnabled
} from '../../../../consts/messages'

interface CoursesTopbarActionsProps {
  slot: ISlot
  params?: ApplyFiltersAndDispatchParams
  isSlotSections?: boolean
  allowedActions?: string[]
  /**
   * Refresh the table on a *different* status view, switching the tab to it. Owned by the page
   * because it also moves the `view` URL param, and it guarantees exactly one refetch either way.
   */
  refreshSectionsForView?: (view: string) => void
  handleExport: () => void
  exportEnrollmentsCSVMutate: (params?: Record<string, any>) => void
  exportInstructorsAssignmentsCSVMutate: (params?: Record<string, any>) => void
  exportStudentSurveySubmissionMutate: (slotId: string, params?: Record<string, any>) => void
}

const CoursesTopbarActions: React.FC<CoursesTopbarActionsProps> = ({
  slot,
  params,
  isSlotSections,
  allowedActions,
  refreshSectionsForView,
  handleExport,
  exportEnrollmentsCSVMutate,
  exportInstructorsAssignmentsCSVMutate,
  exportStudentSurveySubmissionMutate
}) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [isBulkUpdateModal, setIsBulkUpdateModal] = useState<boolean>(false)
  const [isResetCourseSectionModal, setIsResetCourseSectionModal] = useState<boolean>(false)
  const [courseSectionsModal, setCourseSectionsModal] = useState<boolean>(false)
  const [isUpdateTemplateRevisionModal, setIsUpdateTemplateRevisionModal] = useState<boolean>(false)
  const [isBulkReopenModal, setIsBulkReopenModal] = useState<boolean>(false)
  const [isBulkGoLiveModal, setIsBulkGoLiveModal] = useState<boolean>(false)
  const { checkIfBlocked, setTask } = useSlotTaskContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const projectId: string = searchParams.get('projectId') || ''
  const queryClient = useQueryClient()
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const view = searchParams.get('view') || ''
  const scope = searchParams.get('scope') || ''
  const isPublicView = scope === 'all'
  const isSlotAnonymous = slot?.isSlotAnonymous ?? false
  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(slot?.isInstructorReleaseOnGradePostingEnabled)
  const csvImportHelpText = isGradePostingReleaseEnabled
    ? `Instructor Release Date: leave blank on create to leave unset. On update, enter ${CSV_INSTRUCTOR_RELEASE_CLEAR_TOKEN} (case-insensitive) to clear the section date; leave blank to preserve the existing value.`
    : undefined

  const slotListWithPermissions = queryClient.getQueryData(['slotDataById', id]) as IDistributionPlan ?? {}
  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const uploadApi = (): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) setIsUploadModal(true) })
  }

  const addManually = (): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) setIsCreateModal(true) })
  }

  const bulkUpdate = (): void => {
    void checkIfBlocked().then((blocked) => { if (!blocked) setIsBulkUpdateModal(true) })
  }

  const canCreateSections = Boolean(allowedActions?.includes(TableModalAction.CREATE))
  // Bulk Update mutates attached sections (same surface as single-section edit) → EDIT.
  // Bulk Add / Upload stay on CREATE. Dropdown shows when either permission is present.
  const canBulkUpdateSections = Boolean(allowedActions?.includes(TableModalAction.EDIT))
  const isSlotClosed = slot.slotNodeDetails.slotStatus === SlotStatus.Closed
  // Dropdown filters on `visible !== false` and ignores `hidden` — use `visible` only.
  const menuItems = [
    { text: 'Upload Course Sections (.csv)', action: uploadApi, visible: canCreateSections },
    { text: 'Add Manually', action: addManually, visible: canCreateSections && !isSlotClosed },
    { text: 'Bulk Update Course Sections', action: bulkUpdate, visible: canBulkUpdateSections && !isSlotClosed }
  ]
  const showAddCourseSectionsMenu = canCreateSections || canBulkUpdateSections

  const { mutate: resetSections, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return await DistributionManager.resetCourseSections(slot?.slotId)
    },
    onSuccess: (response) => {
      notification(response?.data?.message, { variant: 'success' })
      void dispatch(getSlotSectionApi({ urlId: slot?.slotId, params }))
      setIsResetCourseSectionModal(false)
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset course sections.', { variant: 'error' })
    },
    meta: { silent: true }

  })

  // `params` carries search/scope/pageSize but not the tab, which lives in the URL. Without it the
  // refetch is unfiltered and the table shows every status under whichever tab is selected — very
  // visible after a bulk reopen, where the rows that just left Closed would reappear on it.
  const refreshSections = (): void => {
    void dispatch(getSlotSectionApi({ urlId: slot?.slotId, params: { ...params, ...(view ? { view } : {}) } }))
  }

  /**
   * Where a finished bulk status change leaves the admin: on **Live & Upcoming**, which is where
   * both operations put their sections. Reopen moves them off the Closed tab it ran from, so
   * refreshing in place would show a list containing none of the work; go live keeps them on the
   * same tab, but the admin may have changed tabs while the task ran, and the point of the switch is
   * to land on the sections that just changed either way.
   *
   * Unconditional, rather than only when the source view emptied (the per-row rule in
   * `shouldSwitchSectionView`): a bulk run has no single acted-on row, and the destination is where
   * the work landed regardless of how much else is left behind. The completion toast is what says
   * how many actually moved.
   *
   * Falls back to refreshing in place if the page did not pass the switcher, so the table is never
   * left stale.
   */
  const showChangedSections = (): void => {
    if (refreshSectionsForView) {
      refreshSectionsForView(viewFromStatus(SlotStatus.Live))
      return
    }
    refreshSections()
  }

  const { mutate: updateTemplateRevisionForSlotMutate, isPending: isUpdatingTemplateRevision } = useMutation({
    mutationFn: async () => await DistributionManager.updateTemplateRevisionForSlot({ slotId: slot?.slotId }),
    onSuccess: (response) => {
      createUpdateTemplateRevisionSuccessHandler({ setTask, refreshSections })(response)
      setIsUpdateTemplateRevisionModal(false)
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to update survey template revision.', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const resetModalText = 'Deletes all course sections in Draft status from the survey project. This action cannot be undone.'
  const showResetButton = (slot.slotNodeDetails.slotStatus !== SlotStatus.Closed || isSlotSections) && allowedActions?.includes(TableModalAction.DELETE)

  const slotStatus = slot?.slotNodeDetails?.slotStatus
  // Sync is editable only for Draft + CREATE/EDIT; all other combinations are read-only.
  const hasSyncPermission = slotListWithPermissions?.actions?.includes(Actions.EDIT) || slotListWithPermissions?.actions?.includes(Actions.CREATE)
  const showSyncCourseButton = slotListWithPermissions?.actions?.includes(Actions.READ) || hasSyncPermission
  const isDraftStatus = slotStatus === SlotStatus.Draft
  const isSyncCourseReadOnly = !isDraftStatus || !hasSyncPermission
  const isSyncCourseButtonDisabled = !showSyncCourseButton

  // • “Auto-import is active” if any sync/filter feature flags are turned on for the slot
  const isAutoImportActive = slot?.isCourseAttributeFilterActive || slot?.isReportingHierarchyFilterActive || slot?.isSyncAllSectionsForTermActive
  const isResetCourseSectionButtonDisabled = slotStatus !== SlotStatus.Draft
  const resetTooltip = !isResetCourseSectionButtonDisabled ? 'Deletes all course sections in Draft status from the survey project. This action cannot be undone.' : 'You can only reset course sections when the survey project is in Draft status.'
  // Project-wide template revision applies to live surveys, so it's only offered for a LIVE slot.
  const showUpdateTemplateRevision = slotStatus === SlotStatus.Live && hasSyncPermission
  const isSelectedViewLiveOrPublished = view === 'live_upcoming'
  const isSelectedViewDraftOrClosed = view === 'draft' || view === 'closed'
  const isSelectedViewClosed = view === 'closed'

  // Bulk Reopen is offered exactly where a reopen is legal: the backend's per-section endpoint
  // requires the project (root node) to be LIVE and the section to be CLOSED, so the item belongs
  // on a LIVE project's Closed tab and nowhere else. Hidden rather than disabled elsewhere,
  // following Update Template Revision above.
  //
  // Reopen requires `SurveyProjectCourseSections.CHANGE_STATUS` — not EDIT, which is what Bulk
  // Update uses. `CHANGE_STATUS` is readable here: the sections list endpoint declares no explicit
  // `actions`, so its top-level array is the caller's own permission set. Gating on the per-row
  // REOPEN_SURVEY would never match, because that one is appended per row, not to the list.
  const canChangeSectionStatus = Boolean(allowedActions?.includes(Actions.CHANGE_STATUS))
  // The backend's own `is_opt_out_setting_enabled` is this pair OR-ed together. Both bulk status
  // actions exclude opted-out sections, and only when the project has opt-out switched on — the
  // same condition the endpoints refuse a section under.
  const isOptOutSettingEnabled = Boolean(slot?.isAcademicUnitOptOutEnabled) || Boolean(slot?.isInstructorOptOutEnabled)
  const showBulkReopen = isBulkReopenEnabled() && slotStatus === SlotStatus.Live && isSelectedViewClosed && canChangeSectionStatus
  // Go Live is the mirror image: the endpoint requires the project LIVE and the section PUBLISHED,
  // and published sections live on the Live & Upcoming tab. Same permission, since both are status
  // changes. The two items are mutually exclusive — each belongs to one tab.
  const showBulkGoLive = isBulkGoLiveEnabled() && slotStatus === SlotStatus.Live && isSelectedViewLiveOrPublished && canChangeSectionStatus

  /**
   * No export is gated on sandbox any more. The full-cycle spec (approved 2026-09-21) withdrew the
   * lifecycle freeze these gates were built on: a sandboxed term goes Live, collects responses and
   * closes, so the Live variants, the submission report and the Closed tab all hold real rows.
   *
   * Its data is kept out of real figures by the backend's exclusion filters, not by refusing the
   * admin who is running the rehearsal the exports they are rehearsing with.
   */
  const cdgExtraMenuItems: DropdownItemConfig[] = [
    // ── EXPORTS ──────────────────────────────────────────────────────────────
    { text: 'EXPORTS', isHeader: true, visible: !isPublicView },

    { text: 'Course Sections (.csv)', action: handleExport, visible: !isPublicView },

    // Draft / Closed view — flat enrollment row
    { text: 'Enrollments (.csv)', action: () => { exportEnrollmentsCSVMutate({ view, slotId: id }) }, visible: isSelectedViewDraftOrClosed && !isPublicView },

    // Live / Published view — sub-label + indented variants
    { text: 'Enrollments', isSubLabel: true, visible: isSelectedViewLiveOrPublished && !isPublicView },
    { text: 'Upcoming (.csv)', action: () => { exportEnrollmentsCSVMutate({ view: 'published', slotId: id }) }, isSubItem: true, visible: isSelectedViewLiveOrPublished && !isPublicView },
    { text: 'Live (.csv)', action: () => { exportEnrollmentsCSVMutate({ view: 'live', slotId: id }) }, isSubItem: true, visible: isSelectedViewLiveOrPublished && !isPublicView },

    // Draft / Closed view — flat instructor row
    { text: 'Instructor Assignments (.csv)', action: () => { exportInstructorsAssignmentsCSVMutate({ view, is_instructor: true, slotId: id }) }, visible: isSelectedViewDraftOrClosed && !isPublicView },

    // Live / Published view — sub-label + indented variants
    { text: 'Instructor Assignments', isSubLabel: true, visible: isSelectedViewLiveOrPublished && !isPublicView },
    { text: 'Upcoming (.csv)', action: () => { exportInstructorsAssignmentsCSVMutate({ view: 'published', is_instructor: true, slotId: id }) }, isSubItem: true, visible: isSelectedViewLiveOrPublished && !isPublicView },
    { text: 'Live (.csv)', action: () => { exportInstructorsAssignmentsCSVMutate({ view: 'live', is_instructor: true, slotId: id }) }, isSubItem: true, visible: isSelectedViewLiveOrPublished && !isPublicView },

    { text: 'Survey Submission Report (.csv)', action: () => { exportStudentSurveySubmissionMutate(id ?? '') }, visible: (view === 'closed' || view === 'live_upcoming') && !isSlotAnonymous && !isPublicView },

    // ── divider ──────────────────────────────────────────────────────────────
    { text: '', isDivider: true, visible: !isPublicView && (showSyncCourseButton || showResetButton || showUpdateTemplateRevision || showBulkReopen || showBulkGoLive) },

    // ── SETTINGS ─────────────────────────────────────────────────────────────
    // Bulk Reopen is the last item in this group and introduces no header or divider of its own.
    { text: 'SETTINGS', isHeader: true, visible: showSyncCourseButton || showResetButton || showUpdateTemplateRevision || showBulkReopen || showBulkGoLive },

    { text: 'Sync Course Sections', action: () => { void checkIfBlocked().then((blocked) => { if (!blocked) setCourseSectionsModal(true) }) }, chip: { label: isAutoImportActive ? 'On' : 'Off', color: isAutoImportActive ? 'success' : 'default' }, disabled: isSyncCourseButtonDisabled, visible: showSyncCourseButton },

    { text: 'Reset Course Sections', action: () => { void checkIfBlocked().then((blocked) => { if (!blocked) setIsResetCourseSectionModal(true) }) }, tooltipTitle: resetTooltip, disabled: isResetCourseSectionButtonDisabled, visible: showResetButton },

    { text: 'Update Template Revision', action: () => { void checkIfBlocked().then((blocked) => { if (!blocked) setIsUpdateTemplateRevisionModal(true) }) }, visible: showUpdateTemplateRevision },

    { text: 'Bulk Reopen Surveys', action: () => { void checkIfBlocked().then((blocked) => { if (!blocked) setIsBulkReopenModal(true) }) }, visible: showBulkReopen },

    { text: 'Bulk Go Live', action: () => { void checkIfBlocked().then((blocked) => { if (!blocked) setIsBulkGoLiveModal(true) }) }, visible: showBulkGoLive }
  ]

  return (
    <>
      {showAddCourseSectionsMenu && <Dropdown menuItems={menuItems} label='Add Course Sections' />}
      <GenericModal
        isOpen={isCreateModal}
        title={'Add Course Section to Survey Project'}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateAndEditCourses handleCloseModal={() => { setIsCreateModal(false) }} slot={slot} />
      </GenericModal>
      <GenericModal
        isOpen={isBulkUpdateModal}
        title={'Bulk Update Course Sections'}
        onClose={() => { setIsBulkUpdateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateAndEditCourses handleCloseModal={() => { setIsBulkUpdateModal(false) }} slot={slot} bulkUpdate />
      </GenericModal>
        <Dropdown
          menuItems={cdgExtraMenuItems}
          dropdownType='action'
          extraButtonProps={{
            style: { p: 0 },
            disabled: isResetting || cdgExtraMenuItems.filter(item => item.isHeader !== true && item.isSubLabel !== true && item.isDivider !== true).every(item => item.visible === false),
            circularProgressStyles: { color: grey[300] },
            buttonType: ButtonType.Ghost
          }}
        />
      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Course Sections" csvHeaderEndpoint="/api/slots/sections/csv_headers" uploadCsvEndpoint={`/api/slots/${slot?.slotId}/sections/`} hasCreatePermission={allowedActions?.includes(TableModalAction.CREATE)} importHelpText={csvImportHelpText} onSuccess={() => {
        notification('Courses successfully uploaded', { variant: 'success' })
        void dispatch(getSlotSectionApi({ urlId: slot?.slotId, params }))
      }} />

      <GenericModal
        isOpen={isResetCourseSectionModal}
        onClose={() => { setIsResetCourseSectionModal(false) }}
        title={'Reset Course Sections'}
        maxWidth='sm'
        onConfirm={async () => { resetSections() }}
        confirmButtonLoading={isResetting}
      >
        {resetModalText}
      </GenericModal>

      <GenericModal
        isOpen={isUpdateTemplateRevisionModal}
        onClose={() => { setIsUpdateTemplateRevisionModal(false) }}
        title={'Update Template Revision'}
        maxWidth='sm'
        action={false}
      >
        <UpdateTemplateRevisionProjectModal
          handleCloseModal={() => { setIsUpdateTemplateRevisionModal(false) }}
          onConfirm={() => { updateTemplateRevisionForSlotMutate() }}
          isSubmitting={isUpdatingTemplateRevision}
        />
      </GenericModal>

      {isBulkReopenModal && (
        // `isReopen` both narrows the listing to this project's closed sections and routes Save to
        // the reopen confirm instead of the bulk-add payload. No `isPresent`: it reads the slot's
        // own section list, where every row is attached by definition.
        <BulkSelectCourseSectionModal
          isOpen={isBulkReopenModal}
          onClose={() => { setIsBulkReopenModal(false) }}
          slotId={slot?.slotId}
          termId={slot?.term?.termId}
          isReopen
          isOptOutSettingEnabled={isOptOutSettingEnabled}
          onCompleted={showChangedSections}
        />
      )}

      {isBulkGoLiveModal && (
        // `view=published` is the only status go-live accepts, and opted-out sections are excluded
        // from the list because the endpoint refuses them.
        <BulkSelectCourseSectionModal
          isOpen={isBulkGoLiveModal}
          onClose={() => { setIsBulkGoLiveModal(false) }}
          slotId={slot?.slotId}
          termId={slot?.term?.termId}
          isGoLive
          isOptOutSettingEnabled={isOptOutSettingEnabled}
          onCompleted={showChangedSections}
        />
      )}

      {courseSectionsModal &&
        <SyncSectionModal
          setCourseSectionsModal={setCourseSectionsModal}
          courseSectionsModal={courseSectionsModal}
          slot={slot}
          projectId={projectId}
          isReadOnly={isSyncCourseReadOnly}
        />
      }

    </>
  )
}

export default CoursesTopbarActions
